/*
 * The value of `?compare=`, which is how a URL asks the app to open two accounts against each
 * other. Stated here because BOTH surfaces write it and only one of them was reading it.
 *
 * The app builds four of these in `App.tsx` and parses them back in `restoreCompare`. The static
 * pages build one, in `story-pages.ts`, for the 37 episode pages — and built it WITHOUT the kind
 * prefix, so every one of them said `?compare=moses--exodus-15-22--quran-7-115` where the app
 * requires `?compare=story:moses--exodus-15-22--quran-7-115`.
 *
 * Nothing failed. `restoreCompare` returns on `indexOf(':') < 0`, so the app opened at its front
 * door with the address bar still carrying the request. The comment above the broken line read
 * "THE APP HAS A VIEW BUILT FOR EXACTLY THIS PAGE" and was correct; the string under it was one
 * prefix short, and no count of links can see the difference between a link that works and a link
 * that resolves.
 *
 * THE WIRE FORMAT DOES NOT CHANGE. The app writes these into the address bar, which means readers
 * have shared them; this module states what was already true and gives both surfaces one door to
 * it. `parse` mirrors the app's original reader exactly, including `indexOf` rather than `split`,
 * because a numeric comparison carries refs of its own — `num:Genesis 18:1~...` has three colons
 * and only the first one divides it.
 */

/** An episode told in both books, opened as its two accounts. */
export type CompareLink =
  | { readonly kind: 'story'; readonly story: string }
  | { readonly kind: 'told'; readonly a: string; readonly b: string }
  | { readonly kind: 'diff'; readonly story: string }
  /*
   * `counted` is what the two figures disagree about — "joab", "asaph". It is optional on the
   * record, and the hand-written template that built this value did not know that: with no
   * `counted` it spelled the word `undefined` into the URL, and the lookup then searched for a
   * comparison counting a thing called undefined. All 14 in the bundle carry one today, so nothing
   * was broken — it was waiting for the first one that did not. The format now says absent by
   * being shorter, which round-trips.
   */
  | { readonly kind: 'numbers'; readonly a: string; readonly b: string; readonly counted?: string };

/** The value to put after `?compare=`, unencoded. Callers encode it for a URL. */
export function compareValue(link: CompareLink): string {
  switch (link.kind) {
    case 'story':
      return `story:${link.story}`;
    case 'told':
      return `told:${link.a}~${link.b}`;
    case 'diff':
      return `diff:${link.story}`;
    case 'numbers':
      return link.counted === undefined
        ? `num:${link.a}~${link.b}`
        : `num:${link.a}~${link.b}~${link.counted}`;
  }
}

/**
 * Read a `?compare=` value back, or `undefined` if it names nothing this app knows.
 *
 * A missing part is a missing link rather than an empty string: `told:a~` asked for a comparison
 * with one side, which is not a comparison.
 */
export function parseCompareValue(value: string): CompareLink | undefined {
  const colon = value.indexOf(':');
  if (colon < 0) return undefined;
  const kind = value.slice(0, colon);
  const id = value.slice(colon + 1);
  if (id.length === 0) return undefined;
  if (kind === 'story') return { kind: 'story', story: id };
  if (kind === 'diff') return { kind: 'diff', story: id };
  if (kind === 'told') {
    const [a, b] = id.split('~');
    return a && b ? { kind: 'told', a, b } : undefined;
  }
  if (kind === 'num') {
    const [a, b, counted] = id.split('~');
    if (!a || !b) return undefined;
    return counted ? { kind: 'numbers', a, b, counted } : { kind: 'numbers', a, b };
  }
  return undefined;
}
